"use client"

/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Fetch the public subscription plan catalogue (locale-aware)
 *   PROPS:   none (hook)
 *   STATE:   plans, loading, error
 *   DEPENDS: M-API-CLIENT (api), next-intl (useLocale)
 *   EMITS:   none
 *   ACCESSIBILITY: n/a (data hook)
 *   LINKS:   M-USE-PRICING
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-06-16] Initial pricing data hook (GET /api/v1/subscriptions/plans)
 */

import { useEffect, useState } from "react"
import { useLocale } from "next-intl"
import { api } from "@/lib/api-client"

export interface Plan {
  code: string
  price_amount: string
  price_currency: string
  billing_period: string
  name: string
  description: string | null
  features: string[]
  is_highlighted: boolean
}

interface PlansPayload {
  plans: Plan[]
}

export function usePricing() {
  const locale = useLocale()
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)

      const res = await api.get(`/api/v1/subscriptions/plans?locale=${encodeURIComponent(locale)}`)
      if (cancelled) return

      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: "Failed to load plans" }))
        setError(body.message ?? "Failed to load plans")
        setPlans([])
      } else {
        const data: PlansPayload = await res.json()
        setPlans(data.plans)
      }
      setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [locale])

  return { plans, loading, error }
}
